import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { TermsContext } from './i18n/TermsContext'
import { VERTICALS, DEFAULT_VERTICAL } from './domain/verticals'
import { isActiveJourney, isPending } from './domain/engine'
import type { Aggregator, CustomerDetails, Ticket, OperatorWork, QueueWait, ReferenceBundle, FieldDef, TicketStatus } from './domain/types'
import { createBackend } from './data'
import { createJourneySchedulingSource } from './data/journeyScheduling'
import { env } from './config/env'
import { usePendingActions } from './store/usePendingActions'
import { readCachedReference, writeCachedReference } from './lib/referenceCache'
import { firstName, fullLocationLabel, numericResourceId, splitLocationLabel } from './lib/labels'
import { readOperatorAttendant, writeOperatorAttendant } from './lib/operatorAttendantStorage'
import { createLatestRequestGuard } from './lib/latestRequest'
import { isOperatorReadyServiceConfig } from './domain/serviceConfigs'
import { useRealtimeSync } from './store/useRealtimeSync'
import { useDebouncedActions } from './store/useDebouncedActions'
import {
  bulkActionKey,
  cancelJourneyDebounceKey,
  cancelTicketDebounceKey,
  journeyActionKey,
  ticketActionKey,
} from './lib/actionKeys'
import { ReferenceProvider } from './data/ReferenceContext'
import { AbortedError, ConflictError, ApiError } from './data/errors'
import type { CreateJourneyInput, JourneyFilter, JourneyPage, JourneyListCounters, JourneyCalendarLoader, OperatorWorkPeriod, UpdateJourneyStepsRequest } from './data/DataSource'
import type { AppActions, ViewState } from './store/types'
import { EMPTY_JOURNEY_LIST_STATE, journeyListRequest } from './features/journeyListFilters'
import { createJourneyConfirmer } from './features/confirmJourney'
import { createRunAction } from './features/runAction'
import { isAnonymousCustomer } from './domain/anonymousCustomer'
import { cachedTransitionFields, cachedCreationFields, invalidatePrefetch } from './data/prefetchCache'
import {
  EMPTY_OPERATOR_COUNTS,
  EMPTY_OPERATOR_WORK,
  filterOperatorWorkByJourneyStatus,
  removeTicketsFromOperatorWork,
  mergeOperatorWorkView,
  reconcileOperatorWorkForStatus,
  reconcileOtherCountByServiceConfig,
  type OperatorWorkCounts,
} from './features/operatorWork'

import { Sidebar, Topbar, canAccessSettings, isOwnerRole } from './components/chrome'
import { useModalManager } from './components/ModalManager'
import { Toasts, Empty, type Toast } from './components/misc'
import { BlockSkeleton } from './components/Skeleton'
import { TickProvider } from './components/TickContext'
import { TweaksPanel, TweakSection, TweakRadio, useTweaks } from './components/TweaksPanel'
import { canShowTweaks } from './components/tweaksVisibility'
import { ListaScreen } from './screens/ListaScreen'
import { DetalhesScreen } from './screens/DetalhesScreen'
import { OperadorScreen } from './screens/OperadorScreen'
import { callableRows, pickPreferential } from './screens/operatorPanel'
import { CancelModal } from './screens/CancelModal'
import { AddStepModal } from './screens/AddStepModal'
import { TransferModal } from './screens/TransferModal'
import { RescheduleModal } from './screens/RescheduleModal'
import { TicketCancelModal } from './screens/TicketCancelModal'
import { TransitionFieldsModal } from './screens/TransitionFieldsModal'
// Lazy-loaded telas: só entram no chunk quando o operador navega pra elas.
// Estimativa: −80–120 kB no bundle inicial (Criar 1189, ModeloEditor 853,
// CustomFieldEditor 413 linhas). Crítico porque essas telas só são acessadas
// raramente (criar atendimento, configurar modelos).
const CriarScreen = lazy(() => import('./screens/CriarScreen').then((m) => ({ default: m.CriarScreen })))
const TicketDetailModal = lazy(() => import('./screens/TicketDetailModal').then((m) => ({ default: m.TicketDetailModal })))
const ModelosScreen = lazy(() => import('./screens/ModelosScreen').then((m) => ({ default: m.ModelosScreen })))
const ModeloEditorScreen = lazy(() => import('./screens/ModeloEditorScreen').then((m) => ({ default: m.ModeloEditorScreen })))
const CustomFieldsScreen = lazy(() => import('./screens/CustomFieldsScreen').then((m) => ({ default: m.CustomFieldsScreen })))
const CustomFieldEditorScreen = lazy(() => import('./screens/CustomFieldEditorScreen').then((m) => ({ default: m.CustomFieldEditorScreen })))
import { AuthFlow } from './auth/AuthFlow'
import { clearAuthSession, loadAuthSession, onAuthSessionExpired, saveAuthSession, type AuthSession } from './auth/session'
import { listLocations } from './auth/scope'

const TWEAK_DEFAULTS = {
  vertical: DEFAULT_VERTICAL,
  blockStyle: 'lock',
  timelineOrientation: 'vertical',
  opLayout: 'kanban',
}

let _toastId = 0
const EMPTY_REFERENCE: ReferenceBundle = {
  services: [],
  serviceConfigs: [],
  sessions: [],
  offerings: [],
  presets: {},
  customerFields: [],
  journeyFields: [],
  availableDays: [],
}

export function App() {
  const [session, setSession] = useState<AuthSession | null>(() => loadAuthSession())
  const [isChangingScope, setIsChangingScope] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)

  useEffect(() => onAuthSessionExpired(() => {
    setSession(null)
    setIsChangingScope(false)
    setSessionExpired(true)
  }), [])

  if (!session) {
    return <AuthFlow key="login" sessionExpired={sessionExpired} onComplete={(next) => {
      setSession(saveAuthSession(next))
      setSessionExpired(false)
    }} />
  }

  if (isChangingScope) {
    return (
      <AuthFlow
        key="scope"
        initialSession={session}
        onCancel={() => setIsChangingScope(false)}
        onComplete={(next) => {
          setSession(saveAuthSession(next))
          setIsChangingScope(false)
        }}
      />
    )
  }

  return (
    <DashboardApp
      key={`${session.providerId}:${session.locationId}:${session.token}`}
      session={session}
      onChangeScope={() => setIsChangingScope(true)}
      onLogout={() => {
        clearAuthSession()
        setSession(loadAuthSession()) // mock → re-entra como demo; real → null (mostra login)
        setIsChangingScope(false)
        setSessionExpired(false)
      }}
    />
  )
}

function DashboardApp({ session, onChangeScope, onLogout }: {
  session: AuthSession
  onChangeScope: () => void
  onLogout: () => void
}) {
  const backend = useMemo(() => createBackend(), [])
  const ds = backend.dataSource
  const rt = backend.realtime

  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS)
  const terms = (VERTICALS[t.vertical] || VERTICALS[DEFAULT_VERTICAL]).terms
  const showTweaks = canShowTweaks(import.meta.env.DEV, import.meta.env.VITE_ENABLE_TWEAKS)

  const [aggs, setAggs] = useState<Aggregator[]>([])
  // true assim que o primeiro listJourneys() retornar (mesmo resultado vazio).
  // Passado para ListaScreen para evitar shimmer infinito quando não há jornadas.
  const [aggsLoaded, setAggsLoaded] = useState(false)
  const [reference, setReference] = useState<ReferenceBundle | null>(null)
  const scheduling = useMemo(() => createJourneySchedulingSource(ds, reference), [ds, reference])
  const [view, setView] = useState<ViewState>({ name: 'lista', aggId: null })
  // Botão voltar do browser deve voltar para a TELA ANTERIOR da app, não sair do site.
  // Cada troca de tela empurra uma entrada de history (view é serializável: nome+ids);
  // popstate restaura a view anterior (fallback: lista). Entradas repetidas são dedupe.
  const viewFromPop = useRef(false)
  const lastViewKey = useRef<string | null>(null)
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      viewFromPop.current = true
      lastViewKey.current = null
      setView((e.state?.view as ViewState | undefined) ?? { name: 'lista', aggId: null })
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  useEffect(() => {
    const key = `${view.name}|${view.aggId ?? ''}|${view.fieldId ?? ''}|${view.fieldAnchor ?? ''}|${view.fieldScope ?? ''}|${view.completeTicketId ?? ''}`
    if (viewFromPop.current) { viewFromPop.current = false; return }
    if (key === lastViewKey.current) { return }
    lastViewKey.current = key
    const state = { view }
    if (!window.history.state?.view) { window.history.replaceState(state, '') }
    else { window.history.pushState(state, '') }
  }, [view])
  const [search, setSearch] = useState('')
  const [selServiceConfigId, setSelServiceConfigId] = useState(0)
  const [selSessionId, setSelSessionId] = useState<number | null>(null) // definida quando o reference carrega
  const [selPeriod, setSelPeriod] = useState<OperatorWorkPeriod | null>(null)
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>('today')
  const effectiveDateFilter = env.singleDay && view.name !== 'lista' ? 'today' : dateFilter
  const [listViewState, setListViewState] = useState(EMPTY_JOURNEY_LIST_STATE)
  const [listRequest, setListRequest] = useState<JourneyFilter>(() => journeyListRequest(EMPTY_JOURNEY_LIST_STATE.filters, EMPTY_JOURNEY_LIST_STATE.group, '', 'today'))
  const [listPage, setListPage] = useState<{ key: string; page: JourneyPage } | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState(false)
  const [listCounts, setListCounts] = useState<JourneyListCounters | null>(null)
  const [listAttendants, setListAttendants] = useState<import('./domain/types').Attendant[]>([])
  const listAbortRef = useRef<AbortController | null>(null)
  const onListQueryChange = useCallback((next: JourneyFilter) => {
    setListRequest((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next)
  }, [])
  // Calendar indicators follow the other criteria, independently of the current page/range.
  const calendarFilterKey = JSON.stringify({ ...listRequest, cursor: undefined, pageSize: undefined,
    dateFilter: undefined, sessionFrom: undefined, sessionTo: undefined })
  const loadJourneyCalendar = useCallback<JourneyCalendarLoader>((month, signal) =>
    ds.getJourneyCalendar(month, JSON.parse(calendarFilterKey) as JourneyFilter, signal), [ds, calendarFilterKey])
  const operatorServiceConfigs = useMemo(
    () => reference?.serviceConfigs.filter(isOperatorReadyServiceConfig) ?? [],
    [reference],
  )
  // trocar de fila reseta a sessão para "Todas" (null)
  const selectServiceConfig = (scid: number) => { setSelServiceConfigId(scid); setSelSessionId(null); setSelPeriod(null) }
  const activeServiceConfigId = useMemo(() => {
    const firstServiceConfigId = operatorServiceConfigs[0]?.serviceConfigId
    if (operatorServiceConfigs.some((item) => item.serviceConfigId === selServiceConfigId)) {return selServiceConfigId}
    return firstServiceConfigId ?? selServiceConfigId
  }, [operatorServiceConfigs, selServiceConfigId])
  const [actingSelection, setActingSelection] = useState<{ serviceConfigId: number; id: string | null } | null>(null)
  const actingResourceId = actingSelection?.serviceConfigId === activeServiceConfigId
    ? actingSelection.id
    : activeServiceConfigId ? readOperatorAttendant(session, activeServiceConfigId) : null
  const setActingResourceId = useCallback((id: string | null) => {
    if (!activeServiceConfigId) {return}
    setActingSelection({ serviceConfigId: activeServiceConfigId, id })
    writeOperatorAttendant(session, activeServiceConfigId, id)
  }, [session, activeServiceConfigId])
  const [filterResourceId, setFilterResourceId] = useState<string | null>(null)
  // Modal state: 9 modais + 3 fields paralelos extraídos para useModalManager
  // (src/components/ModalManager.tsx). API tipada, lazy-load de fields, cleanup
  // automático — antes eram 12 useState espalhados, cada setter exposto como
  // setX qualquer para 12+ callsites.
  const modal = useModalManager({
    ds,
    getCancelContext: () => {
      const agg = cancelFor ? (detailAgg?.aggregatorId === cancelFor ? detailAgg : aggs.find((a) => a.aggregatorId === cancelFor)) : null
      return agg ? { templateId: agg.metadata.templateId, customerId: agg.cliente.id } : null
    },
  })
  const {
    cancelFor, transferFor, rescheduleFor, detailFor, ticketCancelFor, transitionReq,
    transitioning, confirmChangeScope, confirmLogout, cancelFields, transferFields, ticketCancelFields,
    openCancelJourney: setCancelFor, openTransfer: setTransferFor, openReschedule: setRescheduleFor,
    openDetails: setDetailFor, openTicketCancel: setTicketCancelFor,
    requestTransition: setTransitionReq, setTransitioning,
    setConfirmChangeScope, setConfirmLogout,
  } = modal
  const [toasts, setToasts] = useState<Toast[]>([])
  // Rastreia os timers de auto-dismiss dos toasts para cancelá-los quando o componente
  // desmonta (ou quando o toast é dispensado manualmente). Sem isso, setState seria
  // chamado em um componente desmontado após navegação/refresh.
  const toastTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const toast = useCallback((msg: string, tone = 'success', code: string | null = null, action?: { label: string; fn: () => void }) => {
    const id = ++_toastId
    const dismiss = () => {
      const t = toastTimersRef.current.get(id)
      if (t !== undefined) {
        clearTimeout(t)
        toastTimersRef.current.delete(id)
      }
      setToasts((p) => p.filter((x) => x.id !== id))
    }
    const wrapped = action ? { label: action.label, fn: () => { dismiss(); action.fn() } } : undefined
    setToasts((p) => [...p, { id, msg, tone, code, action: wrapped }])
    const timeoutId = setTimeout(dismiss, action ? 6000 : 3600) // janela maior quando há "Desfazer"
    toastTimersRef.current.set(id, timeoutId)
  }, [])

  // Cleanup dos timers de auto-dismiss quando o App desmonta — evita setState em componente morto.
  useEffect(() => {
    const timers = toastTimersRef.current
    return () => {
      for (const t of timers.values()) {
        clearTimeout(t)
      }
      timers.clear()
    }
  }, [])

  // Ações otimistas com janela de desfazer + fila offline (src/store/useDebouncedActions.ts).
  // windowMs alinhado à vida do toast com 'Desfazer' (toast() usa 6000ms quando há action) —
  // evita botão Desfazer no-op silencioso entre 5s e 6s (audit modais #3).
  const { register: registerDebouncedAction, pendingKeys, flushKey: flushDebouncedKey } = useDebouncedActions({ notify: toast, windowMs: 6000 })
  const [addStepFor, setAddStepFor] = useState<string | null>(null)
  const [addStepSubmitting, setAddStepSubmitting] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // confirmChangeScope / confirmLogout / setConfirmChangeScope / setConfirmLogout
  // vêm do useModalManager (mais acima). NÃO redefinir aqui.
  const [calling, setCalling] = useState<null | 'next' | 'pref'>(null) // trava a chamada em voo (UI)
  const callingRef = useRef(false) // guarda síncrona: barra reentrada no mesmo tick
  const callInFlightRef = useRef<Map<number, Promise<void>>>(new Map()) // call em voo — start otimista aguarda
  const startedInFlightRef = useRef<Map<number, Promise<void>>>(new Map()) // start em voo — complete otimista aguarda
  const confirmedInFlightRef = useRef<Map<number, Promise<void>>>(new Map()) // confirm em voo — check otimista aguarda
  const viewNameRef = useRef<string>('lista') // ref síncrona para o nome da view atual (uso no reload)
  viewNameRef.current = view.name
  const viewAggIdRef = useRef<string | null>(null)
  viewAggIdRef.current = view.aggId || null
  const viewReturnToRef = useRef<'operador' | 'detalhes' | null | undefined>(null)
  viewReturnToRef.current = view.returnTo
  const reloadGuardRef = useRef(createLatestRequestGuard())
  // B-3: navegação lista->detalhes->editar com View Transitions quando o browser suporta.
  // Fallback: setView direto (sem animação). flushSync força DOM sync dentro do callback.
  // P-4 helper (usado por setViewAnimated e pelos reconciles de opWork): aplica a
  // mutação de estado dentro de document.startViewTransition quando suportado;
  // respeita prefers-reduced-motion.
  const animateDom = useCallback((apply: () => void) => {
    const doc = document as Document & { startViewTransition?: (update: () => void) => unknown }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    if (!reduced && typeof doc.startViewTransition === 'function') {
      // eslint-disable-next-line @eslint-react/dom-no-flush-sync
      doc.startViewTransition(() => { flushSync(apply) })
    } else {
      apply()
    }
  }, [])
  const setViewAnimated = useCallback((next: ViewState) => {
    animateDom(() => setView(next))
  }, [animateDom])
  const aggsRef = useRef<Aggregator[]>([])
  aggsRef.current = aggs
  const [flashId, setFlashId] = useState<string | null>(null)
  const [opWork, setOpWork] = useState<OperatorWork | null>(null)
  const [opCounts, setOpCounts] = useState<OperatorWorkCounts | null>(null)
  const [queueWaits, setQueueWaits] = useState<QueueWait[]>([])
  const [semaphoreLimits, setSemaphoreLimits] = useState<{ yellow: number; red: number } | null>(env.dataSource === 'mock' ? { yellow: 3, red: 15 } : null)
  const [operatorRefreshKey, setOperatorRefreshKey] = useState(0)
  const opWorkSelectionRef = useRef<string | null>(null)
  const [detailAgg, setDetailAgg] = useState<Aggregator | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  // Spinners das telas: chaves de ação em voo (lib/actionKeys) e jornadas em confirmação.
  // Os leitores trocam de referência quando o estado muda — é o que permite o useMemo de
  // `actions` depender deles em vez de depender do estado bruto e ficar preso no valor antigo.
  const {
    isConfirmingJourney,
    setJourneyConfirming,
    isActionPending,
    setActionPending,
  } = usePendingActions()
  const [, forceTick] = useState(0)
  const fallbackLocation = splitLocationLabel(session.locationName || `Unidade ${session.locationId}`, session.locationAddress)
  const [resolvedLocation, setResolvedLocation] = useState(fallbackLocation)

  const updateTicketStatusOtimista = useCallback((ticketId: number, status: TicketStatus) => {
    animateDom(() => setOpWork((prev) => prev ? reconcileOperatorWorkForStatus(prev, ticketId, status) : prev))
    setAggs((prev) =>
      prev.map((a) => {
        if (a.tickets.some((t) => t.ticketId === ticketId)) {
          return {
            ...a,
            tickets: a.tickets.map((t) =>
              t.ticketId === ticketId ? { ...t, status } : t
            ),
          }
        }
        return a
      })
    )
    setDetailAgg((prev) => {
      if (prev && prev.tickets.some((t) => t.ticketId === ticketId)) {
        return {
          ...prev,
          tickets: prev.tickets.map((t) =>
            t.ticketId === ticketId ? { ...t, status } : t
          ),
        }
      }
      return prev
    })
  }, [animateDom])

  const moveTicketInOperatorWork = useCallback((ticketId: number, t: Ticket, list: 'eligible' | 'blocked') => {
    animateDom(() => setOpWork((prev) => {
      if (!prev) {return prev}
      const existing = [...prev.serving, ...prev.eligible, ...prev.blocked].find((row) => row.t.ticketId === ticketId)
      if (!existing) {return prev}
      const a: Aggregator = { ...existing.a, tickets: existing.a.tickets.map((ticket) => ticket.ticketId === ticketId ? t : ticket) }
      const row = { ...existing, a, t, info: { podeIniciar: Boolean(t.podeIniciar), motivo: t.motivoBloqueio ?? null, bloqueadoPor: t.bloqueadoPor ?? [], razao: t.razao ?? null } }
      return {
        ...prev,
        serving: prev.serving.filter((item) => item.t.ticketId !== ticketId),
        eligible: list === 'eligible'
          ? [...prev.eligible.filter((item) => item.t.ticketId !== ticketId), row]
          : prev.eligible.filter((item) => item.t.ticketId !== ticketId),
        blocked: list === 'blocked'
          ? [...prev.blocked.filter((item) => item.t.ticketId !== ticketId), row]
          : prev.blocked.filter((item) => item.t.ticketId !== ticketId),
      }
    }))
  }, [animateDom])

  const applyActiveDebounces = useCallback((items: Aggregator[]): Aggregator[] => {
    // Só a presença da chave importa: o valor otimista vive no ref do hook de debounce.
    const pending = new Set(pendingKeys())
    return items.map((agg) => {
      let aggStatus = agg.status
      const aggCancelDebounce = pending.has(cancelJourneyDebounceKey(agg.aggregatorId))
      if (aggCancelDebounce) {
        aggStatus = 'Canceled'
      }

      return {
        ...agg,
        status: aggStatus,
        tickets: agg.tickets.map((t) => {
          let ticketStatus = t.status
          
          const ticketCancelDebounce = pending.has(cancelTicketDebounceKey(t.ticketId))
          if (ticketCancelDebounce) {
            ticketStatus = 'Canceled'
          }

          if (aggCancelDebounce && (isPending(t.status) || t.status === 'Called' || t.status === 'Started')) {
            ticketStatus = 'Canceled'
          }

          return { ...t, status: ticketStatus }
        })
      }
    })
  }, [pendingKeys])

  const reload = useCallback(async (force = true): Promise<{ items: Aggregator[]; detail?: Aggregator | null }> => {
    if (!force) {
      const inflight = reloadInflightRef.current
      if (inflight && inflight.key === JSON.stringify([viewNameRef.current, effectiveDateFilter, listRequest])) {return inflight.promise}
    }
    const exec = async (): Promise<{ items: Aggregator[]; detail?: Aggregator | null }> => {
      // A resposta mais nova vence: o reload dispara em boot, rajada de realtime, pós-ação
      // e retentativa. Sem guarda, a resposta de uma chamada antiga podia chegar depois da
      // nova e pintar a tela com estado velho.
      const token = reloadGuardRef.current.begin()
      listAbortRef.current?.abort()
      const controller = new AbortController()
      listAbortRef.current = controller
      const loadList = async () => {
        if (viewNameRef.current !== 'lista') {return ds.listJourneys({ dateFilter: effectiveDateFilter }, controller.signal)}
        setListLoading(true)
        setListError(false)
        const request = { ...listRequest, dateFilter: listRequest.sessionFrom || listRequest.sessionTo ? 'all' as const : effectiveDateFilter }
        try {
          const page = await ds.listJourneyPage(request, controller.signal)
          if (reloadGuardRef.current.isCurrent(token)) {setListPage({ key: JSON.stringify(request), page })}
          return page.items
        } catch (error) {
          if (reloadGuardRef.current.isCurrent(token) && !(error instanceof AbortedError)) {setListError(true)}
          throw error
        } finally {
          if (reloadGuardRef.current.isCurrent(token)) {setListLoading(false)}
        }
      }
      // P-1/P-2: invalidação pós-ação — cache de form-fields pode ficar velho após mutação.
      invalidatePrefetch()
      // G-1: SWR — se já existe lista pintada (não boot), repinta o cache atual (com debounces
      // reaplicados) ANTES do refetch em background, para a primeira pintura nunca ficar vazia
      // durante o reload. Boot (aggs vazio) continua exibindo skeleton via firstLoad/aggsLoaded.
      // Single-flight (D-2) e force param mantidos como estavam.
      if (aggsRef.current.length > 0) {setAggs(applyActiveDebounces(aggsRef.current))}
      const aggId = viewAggIdRef.current
      // D-3: lista + detalhe aberto em PARALELO (antes, o detalhe esperava a lista).
      const [fresh, freshDetail] = await Promise.all([
        loadList(),
        viewNameRef.current === 'detalhes' && aggId
          ? ds.getJourney(aggId).catch(() => null)
          : Promise.resolve(undefined),
      ])
      if (!reloadGuardRef.current.isCurrent(token)) {
        // Resposta velha: não pinta o estado, mas registra que algum fetch completou —
        // senão o esqueleto da lista ficaria preso caso a chamada mais nova falhe.
        setAggsLoaded(true)
        return { items: fresh }
      }
      setAggs(applyActiveDebounces(fresh))
      setAggsLoaded(true) // sinaliza que o primeiro (e futuros) fetch completou, mesmo que fresh=[]
      if (viewNameRef.current === 'operador') {setOperatorRefreshKey((n) => n + 1)}
      if (freshDetail && viewAggIdRef.current === aggId) {
        setDetailAgg(applyActiveDebounces([freshDetail])[0] ?? null)
      }
      return { items: fresh, detail: freshDetail === undefined ? undefined : freshDetail }
    }
    const promise = exec().catch((error: unknown) => {
      // Outra consulta assumiu a atualização (filtro, navegação ou ação concorrente).
      // Esse cancelamento não pode virar falha de uma mutação já concluída nem
      // disparar outro reload no rollback, cancelando a consulta que a substituiu.
      if (error instanceof AbortedError) {return { items: aggsRef.current }}
      throw error
    }).finally(() => {
      if (reloadInflightRef.current && reloadInflightRef.current.promise === promise) {
        reloadInflightRef.current = null
      }
    })
    reloadInflightRef.current = { key: JSON.stringify([viewNameRef.current, effectiveDateFilter, listRequest]), promise }
    return promise
  }, [ds, applyActiveDebounces, effectiveDateFilter, listRequest])

  const reloadRef = useRef<(force?: boolean) => ReturnType<typeof reload>>(reload)
  const reloadInflightRef = useRef<{ key: string; promise: Promise<{ items: Aggregator[]; detail?: Aggregator | null }> } | null>(null)
  reloadRef.current = reload

  useEffect(() => {
    void reloadRef.current(false).catch((error) => {
      if (!(error instanceof AbortedError)) {toast('Falha ao carregar atendimentos.', 'error')}
    }).finally(() => setAggsLoaded(true))
  }, [ds, effectiveDateFilter, listRequest, view.name, toast])

  useEffect(() => () => listAbortRef.current?.abort(), [])

  useEffect(() => {
    if (view.name !== 'lista' || !listPage) {return}
    const controller = new AbortController()
    let alive = true
    const request = JSON.parse(listPage.key) as JourneyFilter
    void ds.listJourneyPage({ ...request, group: 'all', cursor: undefined, pageSize: 1 }, controller.signal)
      .then((page) => {if (alive) {setListCounts(page.counters)}})
      .catch(() => { /* mantém os últimos totais; sem resposta inicial, continuam desconhecidos */ })
    return () => {alive = false; controller.abort()}
  }, [ds, listPage, view.name])

  useEffect(() => {
    let alive = true
    void ds.getAttendants().then((items) => {if (alive) {setListAttendants(items)}}).catch(() => {})
    return () => {alive = false}
  }, [ds])

  // Recarrega o catálogo (reference) após CRUD de campos customizados, para que
  // customerFields/journeyFields (resolvidos no boot) reflitam a edição na mesma sessão.
  // G-2: SWR — pintura imediata vem do estado (cache em memória) e a revalidação roda em
  // background: os callers não bloqueiam a navegação esperando o refetch. TTL de ~10min:
  // catálogo muda raro, refetch repetido em janela curta é descartado; CRUD de campos
  // passa force=true (definições novas têm que refletir já). Boot-error path (cache
  // local) permanece no effect de boot, intacto.
  const REFERENCE_TTL_MS = 10 * 60 * 1000
  const refFetchedAtRef = useRef(0)
  const refreshReference = useCallback(async (force = false) => {
    if (!force && Date.now() - refFetchedAtRef.current < REFERENCE_TTL_MS) {return}
    try {
      const ref = await ds.getReferenceData()
      setReference(ref)
      writeCachedReference(session.providerId, session.locationId, ref)
      refFetchedAtRef.current = Date.now()
    } catch {
      /* mantém o catálogo atual */
    }
  }, [ds, session.providerId, session.locationId])

  useEffect(() => {
    let alive = true
    setResolvedLocation(fallbackLocation)
    if (env.dataSource !== 'real') {return}
    setSemaphoreLimits(null)
    listLocations(session.token, session.providerId)
      .then((locations) => {
        const selected = locations.find((location) => location.id === session.locationId)
        if (alive && selected?.name) {setResolvedLocation({ name: selected.name, address: selected.address })}
        if (alive && selected?.minutesToSemaphoreYellow && selected.minutesToSemaphoreRed) {
          setSemaphoreLimits({ yellow: selected.minutesToSemaphoreYellow, red: selected.minutesToSemaphoreRed })
        }
      })
      .catch(() => { /* mantém o rótulo salvo na sessão */ })
    return () => { alive = false }
  }, [fallbackLocation.name, fallbackLocation.address, session.locationId, session.providerId, session.token])

// Lazy-load de OnCancel/OnTransfer fields foi movido para useModalManager.
  // (3 useEffects removidos: cancelFields, ticketCancelFields, transferFields)

  // quando o reference carrega: valida a fila selecionada (cai pra 1ª se inexistente) e fixa a sessão
  useEffect(() => {
    if (!reference) {return}
    const cfgs = operatorServiceConfigs
    const scid = cfgs.some((c) => c.serviceConfigId === selServiceConfigId) ? selServiceConfigId : (cfgs[0]?.serviceConfigId ?? selServiceConfigId)
    if (scid !== selServiceConfigId) {setSelServiceConfigId(scid)}
    // Inicializa como null ("Todas")
    // setSelSessionId(null) já é o estado inicial, mas forçamos caso seja reload via hot-swap
    if (selSessionId !== null && !reference.sessions.some(s => s.sessionId === selSessionId && s.serviceConfigId === scid)) {
      setSelSessionId(null)
    }
  }, [operatorServiceConfigs, reference, selServiceConfigId, selSessionId])

  const visibleAggregatorIds = useMemo(() => {
    const ids = new Set<string>()
    if (view.aggId) {ids.add(view.aggId)}
    aggs.forEach((a) => {
      if (a.aggregatorId) {ids.add(a.aggregatorId)}
    })
    return [...ids]
  }, [aggs, view.aggId])

  const visibleTicketIds = useMemo(() => {
    const ids = new Set<number>()
    if (detailAgg) {
      detailAgg.tickets.forEach((t) => {
        if (t.ticketId) {ids.add(t.ticketId)}
      })
    }
    aggs.forEach((a) => {
      a.tickets.forEach((t) => {
        if (t.ticketId) {ids.add(t.ticketId)}
      })
    })
    return [...ids]
  }, [aggs, detailAgg])

  const visibleAggregatorIdsKey = visibleAggregatorIds.join(',')
  const visibleTicketIdsKey = visibleTicketIds.join(',')

  useEffect(() => {
    if (activeServiceConfigId) {rt.watchServiceConfig?.(activeServiceConfigId)}
    if (!reference) {return}
    rt.watchChannels?.(
      reference.serviceConfigs.map((item) => item.serviceConfigId),
      reference.sessions.map((item) => item.sessionId),
      visibleAggregatorIds,
      visibleTicketIds
    )
  }, [activeServiceConfigId, reference, rt, visibleAggregatorIdsKey, visibleTicketIdsKey])


  // Badges das salas: contagem elegível de todas as filas vem de um único /operator-work/counts
  // (paridade de regras com o operator-work por fila garantida pelo backend); a lista completa
  // (serving/eligible/blocked) segue vindo só da fila ativa.
  useEffect(() => {
    if (view.name !== 'operador') {return}
    if (!operatorServiceConfigs.length || !activeServiceConfigId) {
      setOpCounts(operatorServiceConfigs.length ? EMPTY_OPERATOR_COUNTS : null)
      setOpWork(EMPTY_OPERATOR_WORK)
      return
    }
    let alive = true
    // Zera a lista só quando a *seleção* muda (fila/sessão/período) — um refresh disparado por
    // transição de ticket ou evento realtime (operatorRefreshKey) reusa a lista atual até a nova
    // resposta chegar, evitando o flicker de sumir/voltar a cada ação do operador.
    const selectionKey = `${activeServiceConfigId}|${selSessionId ?? ''}|${selPeriod ?? ''}`
    if (opWorkSelectionRef.current !== selectionKey) {
      opWorkSelectionRef.current = selectionKey
      setOpWork(null)
    }
    Promise.all([
      ds.getOperatorWork(activeServiceConfigId, selSessionId, selSessionId == null ? selPeriod : null).catch(() => null),
      ds.getOperatorWorkCounts(selSessionId).catch(() => null),
    ])
      .then(([active, counts]) => {
        if (!alive) {return}
        if (active == null && counts == null) {
          toast('Erro ao carregar painel do operador.', 'error')
          setOpCounts(EMPTY_OPERATOR_COUNTS)
          setOpWork(EMPTY_OPERATOR_WORK)
          return
        }
        setOpCounts(counts ? {
          ...counts,
          otherCountByServiceConfig: reconcileOtherCountByServiceConfig(aggsRef.current, counts),
        } : EMPTY_OPERATOR_COUNTS)
        setOpWork(active ? filterOperatorWorkByJourneyStatus(active, aggsRef.current) : EMPTY_OPERATOR_WORK)
      })
      .catch(() => {
        if (!alive) {return}
        setOpCounts(EMPTY_OPERATOR_COUNTS)
        setOpWork(EMPTY_OPERATOR_WORK)
      })
    return () => { alive = false }
  }, [activeServiceConfigId, ds, view.name, selSessionId, selPeriod, reference, operatorRefreshKey, operatorServiceConfigs])

  useEffect(() => {
    if (view.name !== 'operador') {return}
    let alive = true
    let requestVersion = 0
    const refresh = () => {
      const version = ++requestVersion
      void ds.getQueueWaits()
        .then((waits) => { if (alive && version === requestVersion) {setQueueWaits(waits)} })
        .catch(() => { if (alive && version === requestVersion) {setQueueWaits([])} })
    }
    refresh()
    const interval = window.setInterval(refresh, 10_000)
    return () => { alive = false; window.clearInterval(interval) }
  }, [ds, view.name, operatorRefreshKey])

  useEffect(() => {
    if ((view.name !== 'detalhes' && view.name !== 'editar-jornada') || !view.aggId) {
      setDetailAgg(null)
      setDetailLoading(false)
      return
    }
    let alive = true
    setDetailAgg(null)
    setDetailLoading(true)
    ds.getJourney(view.aggId)
      .then((agg) => { if (alive) {setDetailAgg(agg ?? null)} })
      .catch(() => { if (alive) {toast('Não foi possível carregar os detalhes do atendimento.', 'error')} })
      .finally(() => { if (alive) {setDetailLoading(false)} })
    return () => { alive = false }
  }, [ds, view.name, view.aggId, toast])

  const findTk = useCallback((ticketId: number): { a: Aggregator; t: Ticket } | undefined => {
    if (detailAgg) {
      const tk = detailAgg.tickets.find((x) => x.ticketId === ticketId)
      if (tk) {return { a: detailAgg, t: tk }}
    }
    for (const a of aggs) { const tk = a.tickets.find((x) => x.ticketId === ticketId); if (tk) {return { a, t: tk }} }
    if (opWork) {
      for (const r of [...opWork.serving, ...opWork.eligible, ...opWork.blocked]) {
        if (r.t.ticketId === ticketId) {return { a: r.a, t: r.t }}
      }
    }
    return undefined
  }, [aggs, detailAgg, opWork])

  const findTkRef = useRef(findTk)
  findTkRef.current = findTk
  const updateTicketStatusOtimistaRef = useRef(updateTicketStatusOtimista)
  updateTicketStatusOtimistaRef.current = updateTicketStatusOtimista

  // Tempo real: presença, supressão por debounce local, status otimista e o coalescedor
  // de rajadas. Viviam inline no efeito de boot; agora em src/store/useRealtimeSync.ts.
  useRealtimeSync({
    realtime: rt,
    pendingDebounceKeys: pendingKeys,
    onSync: () => { void reloadRef.current(false) }, // D-2: coalesce com inflight
    onPresenceChanged: () => forceTick((n) => n + 1),
    findTicket: (ticketId) => {
      const found = findTkRef.current(ticketId)
      return found ? { status: found.t.status } : undefined
    },
    onOptimisticStatus: (ticketId, status) => updateTicketStatusOtimistaRef.current(ticketId, status),
  })

  // Boot: falha de autenticação encerra a sessão; outras falhas podem usar o catálogo local.
  useEffect(() => {
    let active = true
    // Default LIGHT; dark só se o usuário escolheu (localStorage). Sistema do OS ignorado.
    let cachedTheme: string | null = null
    try { cachedTheme = localStorage.getItem('fz-theme') } catch { /* modo privado / storage bloqueado */ }
    if (cachedTheme === 'dark') {
      document.documentElement.classList.add('dark-mode')
      document.documentElement.classList.remove('light-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
      document.documentElement.classList.add('light-mode')
    }

    ds.getReferenceData().then((ref) => {
      if (!active) {return}
      setReference(ref)
      refFetchedAtRef.current = Date.now()
      writeCachedReference(session.providerId, session.locationId, ref)
    }).catch((error: unknown) => {
      if (!active || (error instanceof ApiError && error.status === 401)) {return}
      // Cache só é usado com o catálogo no formato atual: payload de build antigo é
      // descartado em vez de entrar no state e estourar `reference.serviceConfigs.map`.
      const cached = readCachedReference(session.providerId, session.locationId)
      if (cached) {
        setReference(cached)
        toast('Modo de exibição offline ativo. Catálogo carregado do cache local.', 'warning')
        return
      }
      setReference(EMPTY_REFERENCE)
      toast('Falha ao carregar o catálogo de serviços.', 'error')
    })
    return () => { active = false }
  }, [ds, toast])

  const runAction = useMemo(() => createRunAction({
    getView: () => ({ name: view.name, aggId: view.aggId }),
    reload,
    setDetailAgg,
    setActionPending,
    toast,
  }), [ds, reload, setActionPending, setDetailAgg, toast, view.aggId, view.name])

  const journeyTicketIds = useCallback(async (aggregatorIds: string[], test: (t: Ticket) => boolean): Promise<number[]> => {
    const local = aggregatorIds.flatMap((aggregatorId) => {
      const agg = aggs.find((item) => item.aggregatorId === aggregatorId)
      return agg?.tickets
        .filter((ticket) => ticket.ticketId > 0 && test(ticket))
        .map((ticket) => ticket.ticketId) ?? []
    })
    if (local.length > 0) {return [...new Set(local)]}

    const remote = await Promise.all(aggregatorIds.map((aggregatorId) => ds.getJourney(aggregatorId)))
    return [...new Set(remote.flatMap((agg) =>
      agg?.tickets
        .filter((ticket) => ticket.ticketId > 0 && test(ticket))
        .map((ticket) => ticket.ticketId) ?? [],
    ))]
  }, [aggs, ds])

  const requestTransition = useCallback(async (
    ticketId: number, trigger: string, title: string,
    run: (fields?: Record<string, string>) => Promise<void>,
  ) => {
    let fields: FieldDef[]
    try { fields = await cachedTransitionFields(ds, ticketId, trigger) } catch { fields = [] }
    if (fields.length === 0) { void run(); return }
    setTransitionReq({ ticketId, title, fields, run })
  }, [ds, setTransitionReq])

  const history = useCallback((id: number) => ds.getTicketHistory(id), [ds])
  const ticketDetails = useCallback((id: number) => ds.getTicketDetails(id), [ds])
  const prevision = useCallback((id: number) => ds.getTicketPrevision(id), [ds])
  const searchCustomers = useCallback((q: string, offset?: number) => offset === undefined ? ds.searchCustomers(q) : ds.searchCustomers(q, offset), [ds])
  const updateCustomerDetails = useCallback(async (customer: CustomerDetails): Promise<CustomerDetails> => {
    const saved = await ds.updateCustomer(customer)
    const patchCustomer = (agg: Aggregator): Aggregator => agg.cliente.id === saved.id
      ? { ...agg, cliente: saved }
      : agg
    setAggs((current) => current.map(patchCustomer))
    setDetailAgg((current) => current ? patchCustomer(current) : current)
    return saved
  }, [ds])
  const creationFields = useCallback((templateId: number | null, customerId: number | null) => cachedCreationFields(ds, templateId, customerId), [ds])

  const applyConfirmedJourney = useCallback((updated: Aggregator) => {
    setAggs((current) => current.map((agg) => agg.aggregatorId === updated.aggregatorId ? updated : agg))
    setDetailAgg((current) => current?.aggregatorId === updated.aggregatorId ? updated : current)
  }, [])

  // Otimismo O-8: patch do status das jornadas (aggs/detailAgg) ANTES do POST;
  // `reload` no rollback é o mesmo padrão que o resto das actions usa no erro.
  const patchJourneyStatusOtimista = useCallback((aggregatorId: string, status: 'Confirmed' | 'Checked') => {
    const from: string[] = status === 'Confirmed' ? ['Opened', 'Authorized'] : ['Confirmed']
    const patch = (a: Aggregator) => ({
      ...a,
      tickets: a.tickets.map((t) => from.includes(t.status) ? { ...t, status } : t),
    })
    setAggs((current) => current.map((agg) => agg.aggregatorId === aggregatorId ? patch(agg) : agg))
    setDetailAgg((current) => current?.aggregatorId === aggregatorId ? patch(current) : current)
  }, [])

  const confirmJourney = useMemo(() => createJourneyConfirmer({
    confirm: (aggregatorId) => ds.confirmJourney(aggregatorId),
    apply: applyConfirmedJourney,
    optimistic: (aggregatorId) => patchJourneyStatusOtimista(aggregatorId, 'Confirmed'),
    rollback: () => { void reload() },
    loading: setJourneyConfirming,
    success: () => { toast('Jornada confirmada.', 'success'); void reload() },
    error: (error) => toast(error instanceof Error ? error.message : 'Não foi possível confirmar a jornada.', 'error'),
  }), [applyConfirmedJourney, patchJourneyStatusOtimista, reload, ds, setJourneyConfirming, toast])


  // Memoizado: `actions` é o contrato inteiro da tela. Sem o memo, a identidade mudaria
  // a cada render do DashboardApp e todo efeito/memo das filhas que lista `actions` nas
  // deps re-executaria junto — por causa de um toast, um modal ou a seleção da fila, que
  // não têm nada a ver com o contrato.
  // Obs.: isto NÃO evita re-render das filhas (não há React.memo em src/screens nem em
  // src/components); o que evita é não mudar o state que elas leem.
  // Deps: tudo que `actions` consome via closure (refs + handlers + state deriv.).
  const actions: AppActions = useMemo(() => ({
    // P-1/P-2: aquece o cache de campos de transição (hover/focus nos botões de ação).
    warmFields: (ticketId, trigger) => { void cachedTransitionFields(ds, ticketId, trigger).catch(() => {}) },
    elapsedOf: (tk) => {
      if (!tk._startedAt) {return 'agora'}
      const m = Math.floor((Date.now() - tk._startedAt) / 60000)
      return m > 0 ? `${m} min` : 'menos de 1 min'
    },
    refresh: () => {
      setActionPending(bulkActionKey('refresh'), true)
      void reload().finally(() => {
        setActionPending(bulkActionKey('refresh'), false)
      })
    },
    call: (ticketId, recall, resourceId) => {
      // Single-flight (audit realtime #10): re-clique em Rechamar/Chamar não dispara 2 POSTs.
      if (callingRef.current || isActionPending(ticketActionKey(ticketId, 'call'))) { return }
      const f = findTk(ticketId)
      const selectedResourceId = numericResourceId(f?.t.resourceId) ?? resourceId ?? actingResourceId
      if (!selectedResourceId) { toast('Selecione um atendente antes de chamar.', 'error'); return }
      // Otimismo: ticket vai pra "Called" imediatamente. recall é no-op visual (já é Called).
      if (f && !recall) {updateTicketStatusOtimista(ticketId, 'Called')}
      // Promise da chamada em voo: o start otimista aguarda a conclusão do POST de call
      // antes de disparar (fila callInFlightRef, ver actions.start).
      const callPromise = runAction(() => ds.call(ticketId, selectedResourceId, { recall }), () => {
        if (recall) {toast(`Rechamando ${f?.t.codigoTicket ?? ''}…`)}
        else if (f) {toast(`Chamado: ${firstName(f.a.cliente.nome)} · ${f.t.nomeServico}`, 'success', f.t.codigoTicket)}
      }, ticketActionKey(ticketId, recall ? 'recall' : 'call'))
      // recall também entra na fila: start durante uma rechamada aguarda o POST terminar.
      callInFlightRef.current.set(ticketId, callPromise.finally(() => callInFlightRef.current.delete(ticketId)))
    },
    callNext: (priorityOnly) => {
      if (!actingResourceId) { toast('Selecione um atendente antes de chamar.', 'error'); return }
      if (callingRef.current) {return} // já há uma chamada em voo — evita duplo-disparo (Enter repetido / clique duplo)
      callingRef.current = true
      setCalling(priorityOnly ? 'pref' : 'next')
      void (async () => {
        try {
          if (priorityOnly) {
            const rows = opWork ? callableRows(opWork, env.singleDay) : []
            const candidate = pickPreferential(rows)
            if (!candidate) {
              toast(priorityOnly ? 'Nenhum preferencial na fila.' : 'Fila vazia — ninguém para chamar.', 'warning')
            } else {
              await ds.call(candidate.t.ticketId, actingResourceId)
              // Otimismo pós-resposta: card aparece antes do reload terminar.
              updateTicketStatusOtimista(candidate.t.ticketId, 'Called')
              await reload()
              toast(`Chamado: ${firstName(candidate.a.cliente.nome)}`, 'success', candidate.t.codigoTicket, {
                label: 'Rechamar',
                fn: () => actions.call(candidate.t.ticketId, true),
              })
            }
          } else {
            const r = await ds.callNext(activeServiceConfigId, actingResourceId, { priorityOnly, sessionId: selSessionId ?? undefined })
            // Otimismo pós-resposta: card aparece antes do reload terminar.
            if (r) {updateTicketStatusOtimista(r.ticketId, 'Called')}
            await reload()
            if (!r) {toast(priorityOnly ? 'Nenhum preferencial na fila.' : 'Fila vazia — ninguém para chamar.', 'warning')}
            else {toast(`Chamado: ${firstName(r.nome)}`, 'success', r.codigoTicket, {
              label: 'Rechamar',
              fn: () => actions.call(r.ticketId, true),
            })}
          }
        } catch (e) {
          if (e instanceof ConflictError) { toast(e.message, 'error'); void reload() }
          else if (e instanceof ApiError) { toast(e.message, 'error') }
          else { toast('Ocorreu um erro. Tente novamente.', 'error') }
        } finally {
          callingRef.current = false
          setCalling(null)
        }
      })()
    },
    start: (ticketId, resourceId) => {
      const f = findTk(ticketId)
      const selectedResourceId = numericResourceId(f?.t.resourceId) ?? resourceId ?? actingResourceId
      if (!selectedResourceId) { toast('Selecione um atendente antes de iniciar.', 'error'); return }
      updateTicketStatusOtimista(ticketId, 'Started')
      // Se a chamada ainda estiver em voo (call optimista), espera ela concluir com sucesso
      // antes de postar o start, evitando 409 por transição fora de ordem no backend.
      const inFlightCall = callInFlightRef.current.get(ticketId)
      const startPromise = runAction(async () => {
        if (inFlightCall) { await inFlightCall }
        await ds.start(ticketId, selectedResourceId)
      }, () => f && toast(`Atendimento iniciado · ${f.t.nomeServico}`), ticketActionKey(ticketId, 'start'))
      // complete otimista aguarda este POST antes de disparar (mesmo esquema call→start).
      startedInFlightRef.current.set(ticketId, startPromise.finally(() => startedInFlightRef.current.delete(ticketId)))
    },
    // Sem janela de "Desfazer": dispara a chamada real na hora, com spinner até o
    // backend confirmar — mesma lógica de cancelTicketConfirm.
    complete: (ticketId) => {
      const f = findTk(ticketId)
      void requestTransition(ticketId, 'OnComplete', 'Concluir atendimento', async (fields) => {
        setTransitionReq(null)
        // Otimismo: ticket vai pra "Completed" imediatamente. Se o backend
        // retornar erro (ex.: conflito 409), o reload() em runAction restaura.
        if (f) {updateTicketStatusOtimista(ticketId, 'Completed')}
        // Se o start ainda está em voo (start otimista), espera o POST concluir antes
        // de postar o complete — evita 409 por transição fora de ordem no backend.
        const inFlightStart = startedInFlightRef.current.get(ticketId)
        void runAction(
          async () => {
            if (inFlightStart) { await inFlightStart }
            await ds.complete(ticketId, fields)
          },
          () => toast(f ? `Concluído · ${f.t.nomeServico}` : 'Atendimento concluído.'),
          ticketActionKey(ticketId, 'complete')
        )
      })
    },
    completeAndAddStep: (ticketId) => {
      // NÃO conclui o ticket aqui — a conclusão é adiada para o fim do submit
      // do editor (CriarScreen), evitando que o backend marque a jornada como
      // Completed (terminal) antes das etapas serem salvas.
      const live = findTk(ticketId)
      const resolvedAggId = (live?.a.aggregatorId as string | undefined)
        ?? (live?.t as unknown as { aggregatorId?: string })?.aggregatorId
        ?? (live?.a as unknown as { aggregatorId?: string })?.aggregatorId
      if (resolvedAggId) {
        setViewAnimated({ name: 'editar-jornada', aggId: resolvedAggId, returnTo: 'operador', completeTicketId: ticketId })
      } else {
        console.warn('[completeAndAddStep] sem aggregatorId para ticketId', ticketId, { live })
        toast('Não foi possível abrir o editor. Tente novamente.', 'error')
      }
    },
    completeTicket: async (ticketId) => {
      try {
        const inFlightStart = startedInFlightRef.current.get(ticketId)
        if (inFlightStart) { await inFlightStart }
        await ds.complete(ticketId)
        toast('Atendimento concluído com sucesso.')
      } catch (e) {
        const message = e instanceof ApiError ? e.message : 'Etapas salvas, mas a conclusão falhou. Tente concluir manualmente.'
        toast(message, 'error')
        throw e
      }
    },
    addJourneyStep: async (aggregatorId, input) => {
      setAddStepSubmitting(true)
      try {
        await ds.addJourneyStep(aggregatorId, input)
        toast('Etapa adicionada.', 'success')
        setViewAnimated({ name: 'detalhes', aggId: aggregatorId })
        await reload()
      } catch (e) {
        if (e instanceof ApiError) { toast(e.message, 'error') }
        else { toast('Ocorreu um erro. Tente novamente.', 'error') }
      } finally {
        setAddStepFor(null)
        setAddStepSubmitting(false)
      }
    },
    updateJourneyCustomer: async (aggregatorId, customerId) => {
      const shouldReturnToOperator = viewReturnToRef.current === 'operador'
      try {
        const updated = await ds.updateJourneyCustomer(aggregatorId, customerId)
        setAggs((prev) => prev.map((a) => a.aggregatorId === aggregatorId ? updated : a))
        setDetailAgg(updated)
        toast('Customer atualizado.', 'success')
        setViewAnimated(shouldReturnToOperator
          ? { name: 'operador' }
          : { name: 'detalhes', aggId: updated.aggregatorId })
      } catch (e) {
        if (e instanceof ApiError) { toast(e.message, 'error') }
        else { toast('Ocorreu um erro. Tente novamente.', 'error') }
      }
    },
    confirm: (ticketId) => {
      void requestTransition(ticketId, 'OnConfirm', 'Confirmar presença', async (fields) => {
        const f = findTk(ticketId)
        // Reopen pendente na janela de Desfazer: commita AGORA — sem isso o backend
        // recusaria o confirm (ticket ainda Canceled no servidor) com 409.
        await flushDebouncedKey(ticketActionKey(ticketId, 'reopen'))
        if (f) {
          moveTicketInOperatorWork(ticketId, {
            ...f.t,
            status: 'Confirmed',
            podeIniciar: false,
            motivoBloqueio: null,
            allowedActions: ['check'],
          }, 'blocked')
        }
        updateTicketStatusOtimista(ticketId, 'Confirmed')
        // check otimista aguarda este POST antes de disparar (mesmo esquema start→complete).
        const confirmPromise = runAction(async () => {
          await ds.confirm(ticketId, fields)
          if (f) {
            const updated = await ds.confirmJourney(f.a.aggregatorId)
            applyConfirmedJourney(updated)
          }
        }, () => { setTransitionReq(null); toast('Presença confirmada.') }, ticketActionKey(ticketId, 'confirm'))
        confirmedInFlightRef.current.set(ticketId, confirmPromise.finally(() => confirmedInFlightRef.current.delete(ticketId)))
        await confirmPromise
      })
    },
    check: (ticketId) => {
      void requestTransition(ticketId, 'OnCheckin', 'Check-in', async (fields) => {
        updateTicketStatusOtimista(ticketId, 'Checked')
        // Se o confirm ainda está em voo (confirm otimista), espera o POST concluir
        // antes de postar o check — evita 409 por transição fora de ordem no backend.
        const inFlightConfirm = confirmedInFlightRef.current.get(ticketId)
        await runAction(async () => {
          if (inFlightConfirm) { await inFlightConfirm }
          await ds.check(ticketId, fields)
        }, () => { setTransitionReq(null); toast('Check-in realizado.') }, ticketActionKey(ticketId, 'check'))
      })
    },
    confirmJourney: (aggregatorId) => {
      void confirmJourney(aggregatorId)
    },
    isConfirmingJourney,
    isTicketActionPending: (ticketId, action) => isActionPending(ticketActionKey(ticketId, action)),
    isJourneyActionPending: (aggregatorId, action) => isActionPending(journeyActionKey(aggregatorId, action)),
    isBulkActionPending: (action) => isActionPending(bulkActionKey(action)),
    checkJourney: (aggregatorId) => {
      // Otimismo: tickets Confirmed → Checked antes do POST (status derivado confiável
      // localmente — mesma fonte do filtro do batch); rollback é o reload do runAction.
      patchJourneyStatusOtimista(aggregatorId, 'Checked')
      void runAction(() => ds.checkJourney(aggregatorId).then(() => {}), () => toast('Check-in da jornada realizado.', 'success'), journeyActionKey(aggregatorId, 'check'))
    },
    confirmJourneys: (aggregatorIds, onSuccess) => {
      if (!aggregatorIds.length) { toast('Nada para confirmar nas jornadas selecionadas.', 'warning'); return }
      void (async () => {
        const pendingKey = bulkActionKey('confirmJourneys')
        setActionPending(pendingKey, true)
        try {
          const ids = await journeyTicketIds(aggregatorIds, (ticket) => ticket.status === 'Opened' || ticket.status === 'Authorized')
          if (!ids.length) { toast('Nada para confirmar nas jornadas selecionadas.', 'warning'); return }
          await runAction(() => ds.confirmTickets(ids), () => {
            toast(`Confirmação de ${aggregatorIds.length} ${aggregatorIds.length > 1 ? 'jornadas' : 'jornada'} realizada.`, 'success')
            onSuccess?.() // audit lista #5: seleção só some quando confirmou de fato
          })
        } catch (err) {
          // Audit modais #6: journeyTicketIds (Promise.all de getJourney) pode lançar —
          // sem catch o clique sumia sem feedback (spinner liberado
          // Audit modais #6: journeyTicketIds (Promise.all de getJourney) pode lançar —
          // sem catch o clique sumia sem feedback (spinner liberado, zero toast).
          if (!(err instanceof ApiError)) { toast('Falha de rede. Tente novamente.', 'error') }
        } finally {
          setActionPending(pendingKey, false)
        }
      })()
    },
    checkJourneys: (aggregatorIds) => {
      if (!aggregatorIds.length) { toast('Nada para check-in nas jornadas selecionadas.', 'warning'); return }
      void (async () => {
        const pendingKey = bulkActionKey('checkJourneys')
        setActionPending(pendingKey, true)
        try {
          const ids = await journeyTicketIds(aggregatorIds, (ticket) => ticket.status === 'Confirmed')
          if (!ids.length) { toast('Nada para check-in nas jornadas selecionadas.', 'warning'); return }
          await runAction(() => ds.checkTickets(ids), () => toast(`Check-in de ${aggregatorIds.length} ${aggregatorIds.length > 1 ? 'jornadas' : 'jornada'} realizado.`, 'success'))
        } catch (err) {
          if (!(err instanceof ApiError)) { toast('Falha de rede. Tente novamente.', 'error') }
        } finally {
          setActionPending(pendingKey, false)
        }
      })()
    },
    updateJourneySteps: async (aggregatorId, request) => {
      const shouldReturnToOperator = viewReturnToRef.current === 'operador'
      const updated = await ds.updateJourneySteps(aggregatorId, request)
      setAggs((current) => current.map((agg) => agg.aggregatorId === updated.aggregatorId ? updated : agg))
      setDetailAgg((current) => current?.aggregatorId === updated.aggregatorId ? updated : current)
      toast('Jornada atualizada.', 'success')
      setViewAnimated(shouldReturnToOperator
        ? { name: 'operador' }
        : { name: 'detalhes', aggId: updated.aggregatorId })
    },
    reopen: (ticketId) => {
      // D-4: fila offline (useDebouncedActions) — reopen é idempotente. Otimismo imediato
      // e commit real após a janela de Desfazer; se offline, entra na fila de retry.
      const f = findTk(ticketId)
      const pendingKey = ticketActionKey(ticketId, 'reopen')
      updateTicketStatusOtimista(ticketId, 'Reopened')
      if (f) {
        moveTicketInOperatorWork(ticketId, {
          ...f.t,
          status: 'Reopened',
          podeIniciar: false,
          motivoBloqueio: 'Ticket reaberto. Confirme novamente para retornar à fila.',
          allowedActions: ['confirm'],
          resourceId: null,
          resourceNome: null,
        }, 'blocked')
      }
      setActionPending(pendingKey, true)
      registerDebouncedAction(
        pendingKey,
        () => {
          void reloadRef.current()
          setActionPending(pendingKey, false)
          setOperatorRefreshKey((n) => n + 1)
          toast('Reabertura desfeita.', 'warning')
        },
        async () => {
          await runAction(() => ds.reopen(ticketId), undefined, pendingKey)
        },
        f ? `Reaberto · ${f.t.nomeServico}` : 'Reaberto',
        'warning',
      )
    },
    reinsert: (ticketId) => {
      // Reinserir no fim da fila (nova previsão) — espelho do botão "Reinserir" do
      // filazero-app (POST tickets/{id}/reinsert). D-4: fila offline (idempotente).
      // Otimismo: o ticket volta pro fim da fila como 'Checked' (a recarga traz
      // a nova previsão e a posição real).
      const f = findTk(ticketId)
      const pendingKey = ticketActionKey(ticketId, 'reinsert')
      updateTicketStatusOtimista(ticketId, 'Checked')
      if (f) {
        moveTicketInOperatorWork(ticketId, {
          ...f.t,
          status: 'Checked',
          timesCalled: 0,
          podeIniciar: true,
          motivoBloqueio: null,
          allowedActions: ['call'],
          resourceId: null,
          resourceNome: null,
        }, 'eligible')
      }
      setActionPending(pendingKey, true)
      registerDebouncedAction(
        pendingKey,
        () => {
          void reloadRef.current()
          setActionPending(pendingKey, false)
          setOperatorRefreshKey((n) => n + 1)
          toast('Reinserção desfeita.', 'warning')
        },
        async () => {
          await runAction(() => ds.reinsert(ticketId), undefined, pendingKey)
        },
        f ? `Reinserido no fim da fila · ${f.t.nomeServico}` : 'Reinserido no fim da fila.',
        'success',
      )
    },
    cancelTicket: (ticketId) => { void cachedTransitionFields(ds, ticketId, 'OnCancel').catch(() => {}); setTicketCancelFor(ticketId) },
    transfer: (ticketId, target, fields) => {
      // Otimismo: fecha o modal imediatamente e remove o ticket do painel
      // ANTES do POST; toast de sucesso só vem após confirm; se o backend
      // falhar, runAction exibe o toast de erro e o reload restaura o estado.
      setTransferFor(null)
      animateDom(() => setOpWork((prev) => prev ? removeTicketsFromOperatorWork(prev, new Set([ticketId])) : prev))
      const pendingKey = ticketActionKey(ticketId, 'transfer')
      setActionPending(pendingKey, true)
      // D-4: fila offline (idempotente). Otimismo: saiu do painel já no clique;
      // undo/offline-restauração via reload.
      registerDebouncedAction(
        pendingKey,
        () => {
          void reloadRef.current()
          setActionPending(pendingKey, false)
          setOperatorRefreshKey((n) => n + 1)
          toast('Transferência desfeita.', 'warning')
        },
        async () => {
          await runAction(() => ds.transfer(ticketId, { ...target, customFields: fields }), undefined, pendingKey)
        },
        'Bilhete transferido.',
        'success',
      )
    },
    reschedule: (ticketId, sessionId) => {
      // Otimismo: idem transfer — fecha modal + toast cedo, reload em background.
      setRescheduleFor(null)
      void runAction(() => ds.reschedule(ticketId, sessionId), () => toast('Ticket reagendado.', 'success'), ticketActionKey(ticketId, 'reschedule'))
    },
    changePriority: (aggregatorId, priority) => {
      // D-4: fila offline (idempotente). Revert restaura reload.
      const pendingKey = journeyActionKey(aggregatorId, 'priority')
      setActionPending(pendingKey, true)
      registerDebouncedAction(
        pendingKey,
        () => {
          void reloadRef.current()
          setActionPending(pendingKey, false)
          toast('Mudança de prioridade desfeita.', 'warning')
        },
        async () => {
          await runAction(() => ds.changePriority(aggregatorId, priority), undefined, pendingKey)
        },
        'Prioridade atualizada.',
        'success',
      )
    },
    history, prevision,
    openTransfer: (ticketId) => { void cachedTransitionFields(ds, ticketId, 'OnTransfer').catch(() => {}); setTransferFor(ticketId) },
    openReschedule: (ticketId) => setRescheduleFor(ticketId),
    openDetails: (ticketId) => setDetailFor(ticketId),
    openAddStep: (aggregatorId) => setAddStepFor(aggregatorId),
    openJourneyEditor: (aggregatorId) => setViewAnimated({ name: 'editar-jornada', aggId: aggregatorId, returnTo: 'detalhes' }),
    printJourney: (aggregatorId) => {
      // Hook de analytics/log: hoje no-op, mas já está no contrato pra
      // permitir futuro log Pusher/Sentry sem refator. console.warn
      // (e não console.info) porque a regra `no-console` do repo só
      // permite warn/error — o log fica no Sentry do operador quando
      // existir, e aqui serve só pra debug durante o desenvolvimento.
      if (typeof window !== 'undefined' && (window as { __journeyPrintDebug?: boolean }).__journeyPrintDebug) {
        console.warn('[journey] print', { aggregatorId })
      }
    },
  }), [
    setActionPending, isActionPending, isConfirmingJourney, reload, toast, findTk,
    actingResourceId, runAction, callingRef, setCalling, opWork, activeServiceConfigId,
    selSessionId, confirmJourney, ds, journeyTicketIds, requestTransition,
    moveTicketInOperatorWork, updateTicketStatusOtimista, setTransitionReq, history, prevision,
    setTransferFor, setRescheduleFor, setDetailFor, setTicketCancelFor, applyConfirmedJourney,
    setAddStepFor, setAddStepSubmitting,
  ])

  const confirmTransition = (values: Record<string, string>) => {
    if (!transitionReq) {return}
    setTransitioning(true)
    void transitionReq.run(values).finally(() => setTransitioning(false))
  }

  const cancelAgg = (aggId: string, motivo: string, fields?: Record<string, string>) => {
    const a = detailAgg?.aggregatorId === aggId ? detailAgg : aggs.find((x) => x.aggregatorId === aggId)
    const affected = a ? a.tickets.filter((tk) => isPending(tk.status) || tk.status === 'Called' || tk.status === 'Started').map((tk) => ({ id: tk.ticketId, status: tk.status })) : []
    
    setCancelFor(null)

    if (affected.length > 0) {
      const prevAggStatus = a?.status
      const prevTicketsStatus = new Map(affected.map((t) => [t.id, t.status]))
      const pendingKey = journeyActionKey(aggId, 'cancel')

      const markCanceled = (item: Aggregator): Aggregator => item.aggregatorId === aggId ? {
        ...item,
        status: 'Canceled',
        tickets: item.tickets.map((t) => prevTicketsStatus.has(t.ticketId) ? { ...t, status: 'Canceled' } : t),
      } : item
      setAggs((prev) => prev.map(markCanceled))
      setDetailAgg((prev) => prev ? markCanceled(prev) : prev)
      // Espelha runAction: o spinner/estado "pendente" começa aqui, não só quando a
      // janela de 5s de "Desfazer" termina e o commit real dispara.
      setActionPending(pendingKey, true)
      // UX: filas do operador refletem o cancelamento no clique (undo recarrega do servidor).
      setOpWork((prev) => prev ? removeTicketsFromOperatorWork(prev, new Set(affected.map((t) => t.id))) : prev)

      registerDebouncedAction(
        cancelJourneyDebounceKey(aggId),
        () => {
          const restoreJourney = (item: Aggregator): Aggregator => item.aggregatorId === aggId ? {
            ...item,
            status: prevAggStatus ?? item.status,
            tickets: item.tickets.map((t) => {
              const original = prevTicketsStatus.get(t.ticketId)
              return original ? { ...t, status: original } : t
            }),
          } : item
          setAggs((prev) => prev.map(restoreJourney))
          setDetailAgg((prev) => prev ? restoreJourney(prev) : prev)
          setActionPending(pendingKey, false)
          setOperatorRefreshKey((n) => n + 1)
          // Audit modais #5: undo também re-sincroniza — realtime de durante os 6s
          // foi suprimido pelo otimismo; volta ao estado do servidor.
          void reloadRef.current(false)
          toast('Cancelamento desfeito.', 'warning')
        },
        async () => {
          await runAction(() => ds.cancelJourney(aggId, motivo, fields), undefined, pendingKey)
        },
        `Atendimento cancelado · motivo: ${motivo}`,
        'warning'
      )
    } else {
      void runAction(() => ds.cancelJourney(aggId, motivo, fields), undefined, journeyActionKey(aggId, 'cancel'))
    }
  }

  // Janela de "Desfazer" (5s) — paridade com cancelJourney. Sem ela, cancelar um
  // bilhete é irreversível pelo simples clique; com ela, o operador tem tempo de
  // notar o erro. O modal fecha IMEDIATAMENTE (operador não fica preso), o
  // otimismo mostra "Canceled" no UI, e o commit real só dispara após a janela.
  // Se undo for clicado, voltamos o status original e o backend nunca é chamado.
  const cancelTicketConfirm = (ticketId: number, motivo: string, fields?: Record<string, string>) => {
    const f = findTk(ticketId)
    const nome = f?.t.nomeServico ? `${f.t.nomeServico} · ` : ''
    const pendingKey = ticketActionKey(ticketId, 'cancel')

    // Fecha o modal de imediato (UX consistente com cancelJourney)
    setTicketCancelFor(null)

    if (!f) {
      // Sem referência local (caso raro — ticket já sumiu da memória), cai no
      // caminho sem-undo para não ficar pendurado.
      void runAction(() => ds.cancelTicket(ticketId, motivo, fields), undefined, pendingKey)
      return
    }

    const prevStatus = f.t.status
    // Otimista: ticket vira "Canceled" já no UI (espelha o cancelJourney).
    updateTicketStatusOtimista(ticketId, 'Canceled')
    setActionPending(pendingKey, true)
    // UX: some das filas do operador no clique (undo recarrega do servidor via operatorRefreshKey).
    animateDom(() => setOpWork((prev) => prev ? removeTicketsFromOperatorWork(prev, new Set([ticketId])) : prev))

    registerDebouncedAction(
      cancelTicketDebounceKey(ticketId),
      () => {
        updateTicketStatusOtimista(ticketId, prevStatus)
        setActionPending(pendingKey, false)
        setOperatorRefreshKey((n) => n + 1)
        void reloadRef.current(false) // audit modais #5: undo tb re-sincroniza
        toast('Cancelamento desfeito.', 'warning')
      },
      async () => {
        await runAction(() => ds.cancelTicket(ticketId, motivo, fields), undefined, pendingKey)
      },
      `${nome}bilhete cancelado · motivo: ${motivo}`,
      'warning',
    )
  }

  const createAgg = async (input: CreateJourneyInput): Promise<void> => {
    try {
      const cliente = input.cliente.id === 0 && !isAnonymousCustomer(input.cliente)
        ? await ds.createCustomer(input.cliente)
        : input.cliente
      const newAgg = await ds.createJourney({ ...input, cliente })
      // Detalhes fetcha no mount da tela — reload não precisa bloquear a navegação.
      setViewAnimated({ name: 'detalhes', aggId: newAgg.aggregatorId })
      setFlashId(newAgg.aggregatorId)
      setTimeout(() => setFlashId(null), 1800)
      toast('Atendimento criado com sucesso', 'success', newAgg.aggregatorId)
      void reload().catch(() => {})
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Não foi possível criar o atendimento.'
      toast(message, 'error')
      throw error
    }
  }

  const onOpen = { create: () => setView({ name: 'criar' }), detalhes: (id: string) => setViewAnimated({ name: 'detalhes', aggId: id }) }
  const activeCount = view.name === 'lista' && listCounts ? listCounts.scheduled + listCounts.serving : aggs.filter((a) => isActiveJourney(a.status, a.tickets)).length
  const navMap: Record<string, string> = { lista: 'lista', criar: 'criar', operador: 'operador', detalhes: 'lista', 'editar-jornada': 'lista', modelos: 'modelos', 'modelo-editor': 'modelos', 'custom-fields': 'custom-fields', 'custom-field-editor': 'custom-fields' }
  const currentAgg = (view.name === 'detalhes' || view.name === 'editar-jornada')
    ? (detailAgg?.aggregatorId === view.aggId ? detailAgg : aggs.find((agg) => agg.aggregatorId === view.aggId))
    : undefined
  const editAgg = view.name === 'editar-jornada' && detailAgg?.aggregatorId === view.aggId
    ? detailAgg
    : undefined
  const transferTk = transferFor != null ? findTk(transferFor) : undefined
  const rescheduleTk = rescheduleFor != null ? findTk(rescheduleFor) : undefined
  const detailTk = detailFor != null ? findTk(detailFor) : undefined
  const ticketCancelTk = ticketCancelFor != null ? findTk(ticketCancelFor) : undefined
  const cancelAggObj = cancelFor != null ? (detailAgg?.aggregatorId === cancelFor ? detailAgg : aggs.find((a) => a.aggregatorId === cancelFor)) : undefined
  const canManageSettings = canAccessSettings(session.role)
  const isOwner = isOwnerRole(session.role)
  const providerName = session.providerName || `Provider ${session.providerId}`
  const locationName = resolvedLocation.name
  const pageLocationLabel = fullLocationLabel(resolvedLocation.name, resolvedLocation.address)
  const userName = session.userName || session.email || 'Usuário'
  const operatorWork = opCounts
    ? mergeOperatorWorkView(opWork ?? EMPTY_OPERATOR_WORK, opCounts)
    : opWork
  const operatorWorkLoading = opWork === null || (activeServiceConfigId !== 0 &&
    opWorkSelectionRef.current !== `${activeServiceConfigId}|${selSessionId ?? ''}|${selPeriod ?? ''}`)

  // gate de boot: app é inútil sem filas/serviços/presets — espera o reference carregar
  if (!reference) {
    return (
      <TickProvider>
        <TermsContext.Provider value={terms}>
          <div className="fz-page" style={{ display: 'grid', placeItems: 'center', minHeight: '70vh', gridColumn: '1 / -1' }}>
            <Empty title="Carregando…" sub="Preparando o atendimento." />
          </div>
        </TermsContext.Provider>
      </TickProvider>
    )
  }

  return (
    <TickProvider>
    <TermsContext.Provider value={terms}>
      <ReferenceProvider bundle={reference}>
      <Sidebar active={navMap[view.name]} activeCount={activeCount}
        providerName={providerName} locationName={locationName}
        user={{ name: userName, email: session.email, role: session.role }}
        onRequestChangeScope={() => {
          setSidebarOpen(false)
          setConfirmChangeScope(true)
        }}
        onLogout={() => {
          setSidebarOpen(false)
          setConfirmLogout(true)
        }}
        isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
        onSelect={(id) => {
        setSidebarOpen(false)
        if (id === 'lista') {setView({ name: 'lista' })}
        else if (id === 'operador') {setView({ name: 'operador' })}
        else if (id === 'criar') {setView({ name: 'criar' })}
        else if (id === 'modelos') {setView({ name: 'modelos' })}
        else if (id === 'custom-fields') {setView({ name: 'custom-fields' })}
      }} />

      <div className="fz-main">
        <Topbar search={search} onSearch={(v) => { setSearch(v); if (v.trim() && view.name !== 'lista') { setView({ name: 'lista' }); toast('Busca ativa em Atendimentos', 'success') } }} contextLabel={pageLocationLabel} onMenuOpen={() => setSidebarOpen(true)} />

        {view.name === 'lista' && <ListaScreen aggregators={aggs} aggsLoaded={aggsLoaded} search={search} onOpen={onOpen} flashId={flashId} actions={actions} locationName={locationName} dateFilter={effectiveDateFilter} onDateFilterChange={setDateFilter}
          listState={{ value: listViewState, onChange: setListViewState }}
          server={{ result: listPage, loading: listLoading, error: listError, counters: listCounts, onQueryChange: onListQueryChange, onRetry: () => {void reloadRef.current().catch(() => {})} }}
          attendants={listAttendants} loadCalendar={loadJourneyCalendar} />}

        {view.name === 'operador' && (
          <OperadorScreen work={operatorWork} loading={operatorWorkLoading} opCounts={opCounts} selServiceConfigId={activeServiceConfigId} onSelServiceConfig={selectServiceConfig}
            selSessionId={selSessionId} onSelSession={setSelSessionId}
            selPeriod={selPeriod} onSelPeriod={(period) => { setSelPeriod(period); setSelSessionId(null) }}
            opLayout={t.opLayout} actions={actions} calling={calling}
            serviceConfigs={operatorServiceConfigs}
            queueWaits={queueWaits} semaphoreLimits={semaphoreLimits}
            attendants={operatorWork?.attendants ?? []} actingResourceId={actingResourceId} onActingResource={setActingResourceId}
            filterResourceId={filterResourceId} onFilterResource={setFilterResourceId} presenceOf={(id) => rt.presenceOf(id)}
            presenceOfWatch={rt.watchServiceConfig ? (scid) => rt.watchServiceConfig!(scid) : undefined} />
        )
        }

        {view.name === 'criar' && <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando o formulário de atendimento." /></div>}><CriarScreen onCancel={() => setView({ name: 'lista' })} onManageTemplates={() => setView({ name: 'modelo-editor' })} onCreate={createAgg} scheduling={scheduling}
          searchCustomers={searchCustomers} createCustomer={(customer) => ds.createCustomer(customer)}
          getJourneyFields={creationFields} getQueueFields={(sessionId) => ds.getQueueFields(sessionId)} getAvailableDays={(scid) => ds.getAvailableDays(scid)} getAvailableDaysBatch={(ids) => ds.getAvailableDaysBatch(ids)} getDaySlots={(scid, date) => ds.getDaySlots(scid, date)} notify={toast} /></Suspense>}

        {view.name === 'detalhes' && currentAgg && (
          <DetalhesScreen agg={currentAgg} orientation={t.timelineOrientation} blockStyle={t.blockStyle}
            onBack={() => setViewAnimated({ name: 'lista' })} onCancelJourney={setCancelFor} actions={actions}
            presence={rt.presenceOf(currentAgg.tickets[0]?.ticketId ?? currentAgg.aggregatorId)}
            getCustomer={(customerId) => ds.getCustomer(customerId)}
            updateCustomer={updateCustomerDetails} />
        )}
        {view.name === 'editar-jornada' && editAgg && (
          <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando o editor da jornada." /></div>}><CriarScreen onCancel={() => (view.returnTo === 'operador' ? setViewAnimated({ name: 'operador' }) : setViewAnimated({ name: 'detalhes', aggId: editAgg.aggregatorId }))} onManageTemplates={() => setView({ name: 'modelo-editor' })} onCreate={createAgg} scheduling={scheduling}
            onUpdateSteps={(aggregatorId: string, request: UpdateJourneyStepsRequest) => actions.updateJourneySteps(aggregatorId, request)} onUpdateCustomer={(aggregatorId: string, customerId: number) => actions.updateJourneyCustomer(aggregatorId, customerId)} editJourney={editAgg}
            searchCustomers={searchCustomers} createCustomer={(customer) => ds.createCustomer(customer)}
            getJourneyFields={creationFields} getQueueFields={(sessionId) => ds.getQueueFields(sessionId)} getAvailableDays={(scid) => ds.getAvailableDays(scid)} getAvailableDaysBatch={(ids) => ds.getAvailableDaysBatch(ids)} getDaySlots={(scid, date) => ds.getDaySlots(scid, date)}
            completeTicketId={view.completeTicketId} onCompleteTicket={(ticketId: number) => actions.completeTicket(ticketId)} notify={toast} /></Suspense>
        )}
        {view.name === 'editar-jornada' && !editAgg && (
          <div className="fz-page">
            {detailLoading
              ? <BlockSkeleton height={180} ariaLabel="Carregando editor" />
              : <Empty title="Jornada não encontrada" />}
          </div>
        )}
        {view.name === 'detalhes' && !currentAgg && (
          <div className="fz-page">
            {detailLoading
              ? <BlockSkeleton height={180} ariaLabel="Carregando atendimento" />
              : <Empty title="Atendimento não encontrado" />}
          </div>
        )}

        {(view.name === 'modelos' || view.name === 'modelo-editor') && !canManageSettings && (
          <div className="fz-page">
            <Empty title="Acesso restrito" sub="Você não tem permissão para acessar esta área." />
          </div>
        )}

        {(view.name === 'custom-fields' || view.name === 'custom-field-editor') && !isOwner && (
          <div className="fz-page">
            <Empty title="Acesso restrito" sub="Você não tem permissão para acessar esta área." />
          </div>
        )}

        {view.name === 'modelos' && canManageSettings && (
          <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando os modelos de jornada." /></div>}><ModelosScreen backend={ds} onChanged={refreshReference} onNavigate={(id) => setView({ name: 'modelo-editor', aggId: id != null ? String(id) : undefined })} /></Suspense>
        )}

        {view.name === 'modelo-editor' && canManageSettings && (
          <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando o editor de modelo." /></div>}><ModeloEditorScreen
            key={view.aggId ?? 'new'}
            templateId={view.aggId ? Number(view.aggId) : null}
            backend={ds}
            services={reference.services}
            serviceConfigs={reference.serviceConfigs}
            onBack={() => setView({ name: 'modelos' })}
            onSaved={() => { void refreshReference(true); setView({ name: 'modelos' }) }}
          /></Suspense>
        )}

        {view.name === 'custom-fields' && isOwner && (
          <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando os campos customizados." /></div>}><CustomFieldsScreen
            backend={ds}
            reference={reference}
            providerName={providerName}
            locationName={locationName}
            isOwner={isOwner}
            onFieldsChanged={refreshReference}
            onNavigate={(id, anchor, scope) => setView({ name: 'custom-field-editor', fieldId: id, fieldAnchor: anchor, fieldScope: scope })}
          /></Suspense>
        )}

        {view.name === 'custom-field-editor' && isOwner && (
          <Suspense fallback={<div className="fz-page"><Empty title="Carregando…" sub="Preparando o editor de campo." /></div>}><CustomFieldEditorScreen
            key={view.fieldId ?? 'new'}
            fieldId={view.fieldId}
            initialAnchor={view.fieldAnchor}
            initialScope={view.fieldScope}
            backend={ds}
            reference={reference}
            locationId={Number(session.locationId) || 0}
            isOwner={isOwner}
            onBack={() => setView({ name: 'custom-fields' })}
            onSaved={(msg) => { toast(msg); void refreshReference(true); setView({ name: 'custom-fields' }) }}
          /></Suspense>
        )}
      </div>

      {confirmChangeScope && (
        <div className="fz-overlay" role="dialog" aria-modal="true" aria-labelledby="scope-modal-title" onClick={() => setConfirmChangeScope(false)}>
          <div className="fz-modal" onClick={(e) => e.stopPropagation()}>
            <p className="fz-modal__title" id="scope-modal-title">Alterar unidade?</p>
            <p className="fz-modal__text">Você será redirecionado para selecionar outra unidade. O contexto atual será encerrado.</p>
            <div className="fz-modal__actions">
              <button className="fz-btn fz-btn--neutral" onClick={() => setConfirmChangeScope(false)}>Cancelar</button>
              <button className="fz-btn fz-btn--primary" onClick={() => { setConfirmChangeScope(false); onChangeScope() }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
      {confirmLogout && (
        <div className="fz-overlay" role="dialog" aria-modal="true" aria-labelledby="logout-modal-title" onClick={() => setConfirmLogout(false)}>
          <div className="fz-modal" onClick={(e) => e.stopPropagation()}>
            <p className="fz-modal__title" id="logout-modal-title">Sair da conta?</p>
            <p className="fz-modal__text">Você vai encerrar a sessão atual neste dispositivo.</p>
            <div className="fz-modal__actions">
              <button className="fz-btn fz-btn--neutral" onClick={() => setConfirmLogout(false)}>Cancelar</button>
              <button className="fz-btn fz-btn--primary" onClick={() => { setConfirmLogout(false); onLogout() }}>Sair</button>
            </div>
          </div>
        </div>
      )}
      {cancelAggObj && <CancelModal agg={cancelAggObj} fields={cancelFields} submitting={isActionPending(journeyActionKey(cancelAggObj.aggregatorId, 'cancel'))} onClose={() => setCancelFor(null)} onConfirm={cancelAgg} />}
      {transferTk && <TransferModal ticket={transferTk.t} fields={transferFields} submitting={isActionPending(ticketActionKey(transferTk.t.ticketId, 'transfer'))} onClose={() => setTransferFor(null)} onConfirm={(target, vals) => actions.transfer(transferTk.t.ticketId, target, vals)} />}
      {rescheduleTk && <RescheduleModal ticket={rescheduleTk.t} submitting={isActionPending(ticketActionKey(rescheduleTk.t.ticketId, 'reschedule'))}
        getAvailableDays={(scid) => ds.getAvailableDays(scid)} getDaySlots={(scid, date) => ds.getDaySlots(scid, date)}
        onClose={() => setRescheduleFor(null)} onConfirm={(sessionId) => actions.reschedule(rescheduleTk.t.ticketId, sessionId)} />}
      {transitionReq && <TransitionFieldsModal title={transitionReq.title} fields={transitionReq.fields} submitting={transitioning} onClose={() => setTransitionReq(null)} onConfirm={confirmTransition} />}
      {detailTk && <Suspense fallback={null}><TicketDetailModal ticket={detailTk.t} agg={detailTk.a} printLocationName={locationName} onClose={() => setDetailFor(null)} loadDetails={ticketDetails}
        canCancel={view.name === 'operador' && (isPending(detailTk.t.status) || detailTk.t.status === 'Called' || detailTk.t.status === 'Started')}
        onCancel={() => { setDetailFor(null); actions.cancelTicket(detailTk.t.ticketId) }} /></Suspense>}
      {ticketCancelTk && <TicketCancelModal ticket={ticketCancelTk.t} cliente={ticketCancelTk.a.cliente} fields={ticketCancelFields} submitting={isActionPending(ticketActionKey(ticketCancelTk.t.ticketId, 'cancel'))}
        onClose={() => setTicketCancelFor(null)} onConfirm={(motivo, vals) => cancelTicketConfirm(ticketCancelTk.t.ticketId, motivo, vals)} />}
      {addStepFor && <AddStepModal aggregatorId={addStepFor} submitting={addStepSubmitting}
        onClose={() => setAddStepFor(null)} onConfirm={(input) => actions.addJourneyStep(addStepFor, input)} />}

      <Toasts toasts={toasts} />

      {showTweaks && (
        <TweaksPanel title="Tweaks">
          <TweakSection label="Vocabulário (vertical)" />
          <TweakRadio label="Entidade atendida" value={t.vertical}
            options={Object.entries(VERTICALS).map(([k, v]) => ({ value: k, label: v.label }))}
            onChange={(v) => setTweak('vertical', v)} />
          <TweakSection label="Indicador de bloqueio" />
          <TweakRadio label="Estilo" value={t.blockStyle} options={['lock', 'hatch', 'dim']} onChange={(v) => setTweak('blockStyle', v)} />
          <TweakSection label="Timeline (Detalhes)" />
          <TweakRadio label="Orientação" value={t.timelineOrientation} options={['vertical', 'horizontal']} onChange={(v) => setTweak('timelineOrientation', v)} />
          <TweakSection label="Painel do operador" />
          <TweakRadio label="Layout" value={t.opLayout} options={['kanban', 'fila']} onChange={(v) => setTweak('opLayout', v)} />
        </TweaksPanel>
      )}
      </ReferenceProvider>
    </TermsContext.Provider>
    </TickProvider>
  )
}
